import { PrioritizationMode, QueueCadenceFormValues, QueueCadenceSettings } from './WorkflowBuilder.types';

/**
 * PRD defaults for a brand new campaign, applied as initial form values on the
 * create path only.
 *
 * These are deliberately not what an absent setting resolves to: the backend
 * reads an absent field as "keep current behaviour" (legacy queue ordering, no
 * daily cap, both toggles off), so seeding them onto an existing workflow that
 * carries no queueCadence would opt it into four settings nobody chose. The
 * edit path starts from QUEUE_CADENCE_UNSET instead.
 */
export const QUEUE_CADENCE_DEFAULTS: QueueCadenceFormValues = {
    prioritizationMode: 'cadence_completeness',
    maxCallsPerDay: '3',
    sameDayEnforcement: true,
    businessDayEnd: '18:00',
    queueDelayAlertThreshold: '10',
    retryTimeShiftEnabled: true,
};

/**
 * The "nothing configured" form state, which is what the edit form shows for a
 * workflow that stores no queueCadence, and for each individual field a
 * partially configured workflow leaves out.
 */
export const QUEUE_CADENCE_UNSET: QueueCadenceFormValues = {
    prioritizationMode: '',
    maxCallsPerDay: '',
    sameDayEnforcement: null,
    businessDayEnd: '',
    queueDelayAlertThreshold: '',
    retryTimeShiftEnabled: null,
};

export const MAX_CALLS_PER_DAY_MIN = 1;
export const MAX_CALLS_PER_DAY_MAX = 10;
export const ALERT_THRESHOLD_MIN = 0;
export const ALERT_THRESHOLD_MAX = 100;

/**
 * What the backend resolves these two settings to when they are not stored
 * (services/cadence/config.go). They are the only two whose fallback matches
 * the PRD default; an unset mode keeps legacy ordering, an unset cap means no
 * cap, and both unset toggles are off. Used for the "leave it blank" copy and
 * for the same-day validation, which the backend runs against the resolved
 * value rather than the stored one.
 */
export const BUSINESS_DAY_END_FALLBACK = '18:00';
export const ALERT_THRESHOLD_FALLBACK = 10;

export const PRIORITIZATION_MODE_OPTIONS: {
    value: PrioritizationMode;
    label: string;
    description: string;
}[] = [
    {
        value: 'cadence_completeness',
        label: 'Cadence completeness',
        description: 'Parties already in the cadence are called before parties with no touchpoints yet.',
    },
    {
        value: 'even_distribution',
        label: 'Even distribution',
        description: 'Outreach is spread across parties: every first call before any second call.',
    },
];

/** Copy shared by the disabled day-boundary controls and their tooltip. */
export const TIMEZONE_REQUIREMENT_MESSAGE =
    'Set an operating hours timezone first: the daily cap, same-day enforcement and business day end all need one to anchor the end of a day.';

/** Copy explaining what an untouched setting in this section actually does. */
export const UNSET_BEHAVIOUR_MESSAGE = `Leave a setting blank to keep this workflow's current behaviour: the queue's existing ordering, no daily cap, and an ${BUSINESS_DAY_END_FALLBACK} business day end with a ${ALERT_THRESHOLD_FALLBACK}% delay alert threshold.`;

/**
 * The daily cap, same-day enforcement and the business day end are the three
 * day-boundary settings, and the backend rejects all three unless the workflow's
 * operating hours resolve to a real timezone. 'OTHER' is the placeholder the
 * timezone Select shows for "reach out to support", so it never resolves.
 */
export const hasOperatingHoursTimezone = (timezone?: string): boolean => !!timezone && timezone !== 'OTHER';

/**
 * Parses a numeric setting out of the string its input holds, or undefined
 * when the field is blank or unparseable, both of which mean "not configured".
 *
 * The blank check is an explicit emptiness test rather than a falsy one
 * because Number('') is 0, and 0 is a valid threshold and an invalid cap. A
 * truthiness check here would quietly turn an untouched field into a stored 0.
 */
const parseSetting = (value: string): number | undefined => {
    if (value.trim() === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

/** 24-hour operating hours start at midnight; otherwise the configured start time. */
export const effectiveOperatingHoursStart = (ctx: {
    operatingHoursEnabled: boolean;
    operatingHoursStartTime: string;
}): string => (ctx.operatingHoursEnabled ? '00:00' : ctx.operatingHoursStartTime);

/** A blank field is unset, which is always allowed; anything filled in is range checked. */
const validateOptionalNumber = (value: string, min: number, max: number): string | true => {
    const parsed = parseSetting(value);
    if (parsed === undefined && value.trim() === '') return true;
    return (
        (parsed !== undefined && Number.isInteger(parsed) && parsed >= min && parsed <= max) ||
        `Enter a whole number between ${min} and ${max}`
    );
};

export const validateMaxCallsPerDay = (value: string): string | true =>
    validateOptionalNumber(value, MAX_CALLS_PER_DAY_MIN, MAX_CALLS_PER_DAY_MAX);

export const validateAlertThreshold = (value: string): string | true =>
    validateOptionalNumber(value, ALERT_THRESHOLD_MIN, ALERT_THRESHOLD_MAX);

/**
 * With same-day enforcement on, a business day end at or before the operating
 * hours start marks the whole operating window as past day end, which defers
 * every call forever. The backend rejects that combination too, and it runs the
 * check against the resolved end, so a blank field is measured against
 * BUSINESS_DAY_END_FALLBACK rather than waved through.
 */
export const validateBusinessDayEnd = (
    value: string,
    ctx: {
        sameDayEnforcement: boolean | null;
        operatingHoursEnabled: boolean;
        operatingHoursStartTime: string;
    }
): string | true => {
    if (value && !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
        return 'Business day end must be a 24-hour HH:MM time';
    }
    if (!ctx.sameDayEnforcement) return true;

    const start = effectiveOperatingHoursStart(ctx);
    if (!value) {
        return (
            BUSINESS_DAY_END_FALLBACK > start ||
            `Set a business day end later than ${start}: an unset one falls back to ${BUSINESS_DAY_END_FALLBACK}, which with same-day enforcement on would defer every call.`
        );
    }
    return value > start || 'Business day end must be later than the operating hours start time';
};

/**
 * Hydrates the six form fields from a stored config. Anything the workflow does
 * not store comes back unset, so an unconfigured field renders blank and stays
 * out of the next save instead of picking up a default nobody asked for.
 */
export const parseQueueCadenceFormData = (qc?: QueueCadenceSettings): QueueCadenceFormValues => ({
    ...QUEUE_CADENCE_UNSET,
    ...(qc?.prioritizationMode ? { prioritizationMode: qc.prioritizationMode } : {}),
    ...(typeof qc?.maxCallsPerDay === 'number' ? { maxCallsPerDay: String(qc.maxCallsPerDay) } : {}),
    ...(typeof qc?.sameDayEnforcement === 'boolean' ? { sameDayEnforcement: qc.sameDayEnforcement } : {}),
    ...(qc?.businessDayEnd ? { businessDayEnd: qc.businessDayEnd } : {}),
    ...(typeof qc?.queueDelayAlertThreshold === 'number'
        ? { queueDelayAlertThreshold: String(qc.queueDelayAlertThreshold) }
        : {}),
    ...(typeof qc?.retryTimeShiftEnabled === 'boolean' ? { retryTimeShiftEnabled: qc.retryTimeShiftEnabled } : {}),
});

/**
 * Builds the config payload, or undefined when the section holds nothing at all.
 *
 * PUT /workflows/:id/config replaces config.queueCadence wholesale with the
 * fields this request carries (it is not a per-field merge) and deletes the key
 * outright for an all-empty object. Returning undefined lets the caller leave
 * the key out of the request entirely, which is the only shape that leaves a
 * stored config untouched, and so the only way a workflow nobody configured
 * still carries no queueCadence after an unrelated edit.
 *
 * The three day-boundary settings are dropped when no operating hours timezone
 * resolves, because the backend rejects the whole request in that case; the
 * other three work without one.
 */
export const buildQueueCadencePayload = (
    values: QueueCadenceFormValues,
    ctx: { operatingHoursTimezone: string }
): QueueCadenceSettings | undefined => {
    const payload: QueueCadenceSettings = {};

    const threshold = parseSetting(values.queueDelayAlertThreshold);

    if (values.prioritizationMode) payload.prioritizationMode = values.prioritizationMode;
    if (threshold !== undefined) payload.queueDelayAlertThreshold = threshold;
    if (typeof values.retryTimeShiftEnabled === 'boolean') {
        payload.retryTimeShiftEnabled = values.retryTimeShiftEnabled;
    }

    if (hasOperatingHoursTimezone(ctx.operatingHoursTimezone)) {
        const cap = parseSetting(values.maxCallsPerDay);
        if (cap !== undefined) payload.maxCallsPerDay = cap;
        if (typeof values.sameDayEnforcement === 'boolean') payload.sameDayEnforcement = values.sameDayEnforcement;
        if (values.businessDayEnd) payload.businessDayEnd = values.businessDayEnd;
    }

    return Object.keys(payload).length > 0 ? payload : undefined;
};
